import React, { useRef } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import useScoreEntry, { TENS_SCORES } from './hooks/useScoreEntry';
import './ScoreInputPad.css';

const DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

// After a key saves and the pad moves to the next player, taps are ignored
// this long. Every key saves AND advances, so a gloved double-tap used to
// score two players with one number.
const LOCKOUT_MS = 250;

/**
 * `group` (optional): everyone on this hole — `[{ id, name, score }]` — drawn
 * as a strip across the top, with `activeId` marked. It is what lets a scorer
 * SEE the hole they are entering: the pad covers the card, and the only thing
 * that used to change after each tap was the name in the header. Tapping a
 * name goes back to that player (`onSelectMember`).
 */
const ScoreInputPad = ({
  onScoreSelect, playerName, holeNumber, holePar, holeSI, onClose,
  group, activeId, onSelectMember, hasScore = true,
}) => {
  const lastTap = useRef(0);
  const guarded = (fn) => (...args) => {
    const now = Date.now();
    if (now - lastTap.current < LOCKOUT_MS) return;
    lastTap.current = now;
    fn(...args);
  };
  const select = guarded(onScoreSelect);
  // Every key on both layers saves the score and advances — see useScoreEntry.
  const { showTens, submit: rawSubmit, openTens, closeTens } =
    useScoreEntry(onScoreSelect, `${playerName}-${holeNumber}`);
  const submit = guarded(rawSubmit);

  return (
    <div className="score-input-pad" onClick={onClose}>
      <div className="score-input-pad-content" onClick={(e) => e.stopPropagation()}>
        <div className="score-input-pad-header">
          <h3>{playerName}</h3>
          <p>Hole {holeNumber} &bull; Par {holePar} &bull; SI {holeSI}</p>
        </div>
        {group && group.length > 1 && (
          <div className="score-input-pad-group" role="list" aria-label={`Hole ${holeNumber} scores`}>
            {group.map((m) => (
              <button
                key={m.id}
                type="button"
                role="listitem"
                className={`sip-member${m.id === activeId ? ' sip-member--active' : ''}`}
                onClick={() => onSelectMember && onSelectMember(m.id)}
                aria-current={m.id === activeId ? 'true' : undefined}
                aria-label={`${m.name}: ${m.score ?? 'no score yet'}`}
              >
                <span className="sip-member-name">{m.name}</span>
                <span className="sip-member-score">{m.score ?? '–'}</span>
              </button>
            ))}
          </div>
        )}
        <div className="score-input-pad-divider"></div>

        <div className="score-input-pad-grid">
          {showTens ? (
            <>
              {TENS_SCORES.map((score) => (
                <button key={score} className="keypad-button keypad-button--tens" onClick={() => submit(score)}>
                  {score}
                </button>
              ))}
              <button
                className="keypad-button keypad-button--util"
                onClick={closeTens}
                aria-label="Back to single figures"
              >
                <FiArrowLeft size={18} />
              </button>
            </>
          ) : (
            <>
              {DIGITS.map((number) => (
                <button key={number} className="keypad-button" onClick={() => submit(number)}>
                  {number}
                </button>
              ))}
              {/* A blow-up hole is rare, so it carries the extra tap rather
                  than every ordinary score carrying a confirmation. */}
              <button className="keypad-button keypad-button--tens-open" onClick={openTens}>
                10 or more
              </button>
            </>
          )}
        </div>

        <div className="score-input-pad-actions">
           {/* Only where there is something to clear: on an empty cell it
               sent a null to the server and read as an action to take. */}
           {hasScore && (
             <button className="keypad-action-button clear-button" onClick={() => select('CLEAR')}>Clear</button>
           )}
           <button className="keypad-action-button ns-button" onClick={() => select('NS')}>NS</button>
           <button className="keypad-action-button done-button" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
};

export default ScoreInputPad;
